'use client';

import {
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  Children,
  isValidElement,
  type ReactNode,
} from 'react';
import { useTheme } from 'next-themes';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronDown, ChevronLeft, X } from 'lucide-react';
import { BottomSheet, usePortalContainer } from '../bottom-sheet';
import { SheetPage, type SheetPageProps } from './SheetNavigator';
import { CollapsibleHeader, type CollapsibleHeaderProps } from './CollapsibleHeader';
import { useCollapsibleHeader } from '../hooks/useCollapsibleHeader';
import { useScrollLock } from '../hooks/useScrollLock';
import type { SheetPageKebabItem } from './SheetPageKebab';
import {
  ResponsiveSheetContext,
  type ResponsiveSheetContextValue,
  type SheetMode,
} from './ResponsiveSheetContext';
import { cn } from '../lib/utils';

export { CollapsibleHeader, type CollapsibleHeaderProps };

// Re-export SheetPage for convenience
export { SheetPage, type SheetPageProps };

// ============================================================================
// Types
// ============================================================================

interface ResponsiveSheetProps {
  /** Whether the sheet is open */
  open: boolean;
  /** Callback when the sheet should close */
  onClose: () => void;
  /** Child SheetPage components */
  children: ReactNode;
  /**
   * Header content rendered in the drag area (sheet mode) or top of modal.
   * Can be a ReactNode or a function that receives context for conditional rendering.
   * In sheet mode, this is the area users can drag to close the sheet.
   */
  header?: ReactNode | ((context: ResponsiveSheetContextValue) => ReactNode);
  /**
   * Pinned below the scroll area in both modes, always visible. For a sheet's
   * commit control ("Save"), which the user must never have to scroll to find.
   *
   * SheetNavigator clips page content with `overflow-clip` (not `hidden`) for
   * its page transitions, so sticky headings inside a page do stick to the
   * sheet's scroll area (2026-10-16). A commit control still belongs here
   * rather than as a sticky element, so it stays visible on every page.
   *
   * Renders on every page. A multi-page sheet whose footer belongs to one page
   * should gate it on the active page itself.
   */
  footer?: ReactNode;
  /** Initial page to display (default: 'main') */
  defaultPage?: string;
  /** Maximum width for modal mode (default: 'max-w-lg') */
  maxWidth?: string;
  /**
   * Where the desktop panel sits: centred (default), or docked to the right
   * edge as a full-height drawer that slides in. Phones keep the bottom sheet.
   */
  placement?: 'center' | 'right';
  /** Breakpoint for switching to modal mode in pixels (default: 768) */
  modalBreakpoint?: number;
  /**
   * Pin the sheet to one presentation regardless of viewport width. Used by
   * hosts that already decide the surface themselves — e.g. MorphMenu /
   * MorphDock render a ResponsiveSheet with `forceMode="sheet"` on mobile so
   * their content gets the full bottom-sheet experience while desktop keeps
   * the morph panel (never this component's modal).
   */
  forceMode?: SheetMode;
  /** Additional class names for the content container */
  className?: string;
  /** Additional class names for the panel (modal or sheet) */
  panelClassName?: string;
  /** Remove default padding from modal panel - useful for edge-to-edge headers */
  noPanelPadding?: boolean;
  /** Maximum height for the bottom sheet on mobile (default: '90dvh') */
  sheetMaxHeight?: string;
  /** Inline styles for the panel (applied to both modal and sheet) */
  panelStyle?: React.CSSProperties;
  /** Hide the built-in close button (useful when header renders its own) */
  hideCloseButton?: boolean;
  /**
   * Dark-glass panel — the app's standard sheet surface. Shorthand for the
   * {@link SHEET_GLASS_PANEL} class string, which was being copy-pasted into
   * every caller that wanted it.
   *
   * Also widens the default `maxWidth`, because glass sheets were passing
   * `max-w-2xl` and reading cramped on a large screen.
   *
   * NOTE: this styles the PANEL, not its contents. A sheet whose interior uses
   * light tokens (`text-foreground`, `bg-muted/30`, `border-border`) will render
   * dark-on-dark. Convert the interior in the same change — white text, white/10
   * surfaces — the way ProfileSheet and the campus sheets do.
   */
  glass?: boolean;
  /**
   * Suppress the built-in "Scroll for more" fog in both postures. For a sheet
   * that draws its own hint: the fog only knows bg-card, bg-white and a dark
   * fallback, so any other panel colour gets a dark wash it never asked for.
   */
  hideScrollIndicator?: boolean;
}

/**
 * The dark-glass sheet surface: translucent near-black, heavy blur, white text.
 * `text-white` is load-bearing — the component keys its own chrome (back button,
 * drag handle, close button) off its presence.
 */
export const SHEET_GLASS_PANEL =
  'bg-neutral-900/85 text-white backdrop-blur-3xl backdrop-saturate-150 supports-[backdrop-filter]:bg-neutral-900/70 overflow-hidden';

// ============================================================================
// Context — defined in ResponsiveSheetContext.ts so SheetPageKebab can
// import it without dragging in the full ResponsiveSheet module (which
// would create a value-level circular import via the kebab → sheet →
// kebab type chain).
// ============================================================================

/**
 * Hook to access ResponsiveSheet context.
 * Provides mode, theme, and navigation utilities.
 */
export function useResponsiveSheet() {
  const context = useContext(ResponsiveSheetContext);
  if (!context) {
    throw new Error('useResponsiveSheet must be used within a ResponsiveSheet');
  }
  return context;
}

// ============================================================================
// Kebab page name — synthetic page injected when a SheetPageKebab pushes
// its menu in sheet mode. Underscored to avoid colliding with consumer
// page names; consumers shouldn't reference it directly.
// ============================================================================

const KEBAB_PAGE_NAME = '__sheetKebabActions';

/**
 * A sheet panel must never be transparent: callers often pass panelClassName
 * for layout tweaks ("overflow-hidden") without realizing it used to REPLACE
 * the default background. If the caller didn't choose a bg- class themselves,
 * keep the default dark panel underneath whatever they passed.
 */
function withPanelBg(panelClassName?: string): string {
  const hasBg = /(^|\s)bg-/.test(panelClassName ?? '');
  return hasBg ? panelClassName! : `bg-[#1f2220] ${panelClassName ?? ''}`.trim();
}

// ============================================================================
// Animation Variants
// ============================================================================

const pageVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? '100%' : '-100%',
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? '-100%' : '100%',
    opacity: 0,
  }),
};

const pageTransition = {
  type: 'tween',
  ease: 'easeInOut',
  duration: 0.25,
};

// ============================================================================
// Internal Components
// ============================================================================

interface SheetContentProps {
  pages: {
    name: string;
    title?: ReactNode;
    backBarTrailing?: ReactNode;
    content: ReactNode;
  }[];
  currentPage: string;
  canGoBack: boolean;
  onBack: () => void;
  mode: SheetMode;
  /** Whether a header is being rendered (used for padding in modal mode) */
  hasHeader?: boolean;
  /** Dark glass panel (panelClassName declares text-white) — the back
   *  strip switches to white text so it stays visible. */
  darkPanel?: boolean;
}

/**
 * Measure an element's height via ResizeObserver. Uses a callback ref
 * so the first measurement happens on attach (not after a useEffect
 * tick where the value would briefly be 0 and animate from a wrong
 * baseline). Returns 0 until the first observation lands; consumers
 * fall back to `height: 'auto'` while that's the case.
 */
function useMeasuredHeight(): [(node: HTMLElement | null) => void, number] {
  const [height, setHeight] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    setHeight(node.getBoundingClientRect().height);
    observerRef.current = new ResizeObserver(([entry]) => {
      if (!entry) return;
      // contentRect excludes border/padding-box; offsetHeight is what
      // the wrapper actually occupies in flow, so prefer that.
      const target = entry.target as HTMLElement;
      setHeight(target.offsetHeight);
    });
    observerRef.current.observe(node);
  }, []);

  return [ref, height];
}

function SheetContent({
  pages,
  currentPage,
  canGoBack,
  onBack,
  hasHeader = false,
  darkPanel = false,
}: SheetContentProps) {
  const activePage = pages.find((p) => p.name === currentPage);
  const direction = currentPage === 'main' ? -1 : 1;

  // Add top padding when there's no header (for modal mode spacing)
  const topPadding = !hasHeader ? 'pt-2' : '';

  // Measure the inner content (back-strip + active page) so the outer
  // wrapper can animate height between sub-pages instead of jumping.
  // mode="popLayout" below pulls exiting elements out of layout flow
  // so the measured height immediately reflects the incoming page,
  // letting the height tween run in parallel with the slide tween.
  // Pattern: Build UI Resizable Panel (Sam Selikoff) — modeled after
  // the panel transitions in Things 3 / Notion / Telegram pickers.
  const [measureRef, contentHeight] = useMeasuredHeight();

  return (
    <motion.div
      // `height: 'auto'` until the first measurement lands so the very
      // first paint isn't 0px (which would briefly collapse the sheet).
      animate={{ height: contentHeight > 0 ? contentHeight : 'auto' }}
      // Bias the spring asymmetric per UX research: growing reads as
      // heavier than shrinking, so a no-bounce spring (~360ms) feels
      // closer to iOS sheet detent transitions than a linear tween.
      transition={{ type: 'spring', bounce: 0, duration: 0.36 }}
      // clip, not hidden: the same cut-off while the height tweens, but
      // `hidden` makes this a scroll container, and sticky headings in a
      // page then stick to it (it never scrolls) instead of to the sheet's
      // scroll area. `clip` is not a scroll container (2026-10-16).
      style={{ overflow: 'clip' }}
      className={`relative ${topPadding}`}
    >
      {/* flow-root: contain child margins (the back-strip's mt-4) so they
          count in offsetHeight. Without it the margin collapses out of this
          div but renders inside the overflow-hidden parent, leaving the
          animated height short and clipping the page's bottom padding. */}
      <div ref={measureRef} className="flow-root">
        {/* Nav strip — shown on any page that has Back navigation OR a
            trailing fact to surface (e.g. a "parent available" readout
            on the root page). Title is optional so pages with their
            own gradient header can skip the label and render just the
            Back chevron. Typography mirrors SectionTitle's subtitle
            (tracking-widest, uppercase) so the strip reads as part of
            the section system. */}
        <AnimatePresence>
          {(canGoBack || activePage?.backBarTrailing) && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="mt-4 mb-4 flex items-center gap-4 px-6"
            >
              {canGoBack && (
                <button
                  onClick={onBack}
                  // Dark glass panels declare text-white in panelClassName —
                  // theme tokens would paint this near-invisible there.
                  className={`-ml-1 flex items-center gap-1 text-xs font-semibold tracking-widest uppercase transition-colors ${
                    darkPanel
                      ? 'text-white/60 hover:text-white'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <ChevronLeft className="h-3.5 w-3.5" />
                  Back
                </button>
              )}
              {canGoBack && activePage?.title && (
                <span
                  className={`min-w-0 flex-1 truncate text-xs font-bold tracking-widest uppercase ${
                    darkPanel ? 'text-white' : 'text-foreground'
                  }`}
                >
                  {activePage.title}
                </span>
              )}
              {activePage?.backBarTrailing && (
                <div className="ml-auto flex shrink-0 items-center gap-2">
                  {activePage.backBarTrailing}
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Page content with slide animation. mode="popLayout" pulls
            exiting children out of layout flow during their exit
            transition so the inner div's measured height reflects only
            the incoming page — that's what makes the height tween land
            in sync with the slide. */}
        <AnimatePresence mode="popLayout" custom={direction}>
          <motion.div
            key={currentPage}
            custom={direction}
            variants={pageVariants}
            initial="enter"
            animate="center"
            exit="exit"
            transition={pageTransition}
          >
            {activePage?.content}
          </motion.div>
        </AnimatePresence>
      </div>
    </motion.div>
  );
}

// ============================================================================
// ResponsiveSheet Component
// ============================================================================

/**
 * A responsive sheet component that displays as a bottom sheet on mobile
 * and a centered modal on desktop.
 *
 * Features:
 * - Automatic viewport detection (mobile = sheet, desktop = modal)
 * - Multi-page navigation with smooth slide transitions
 * - Theme-aware styling based on user preference
 * - Optional header that doubles as drag area on mobile
 * - Shared API for both display modes
 *
 * @example
 * ```tsx
 * <ResponsiveSheet
 *   open={isOpen}
 *   onClose={handleClose}
 *   header={(ctx) => ctx.currentPage === 'main' ? <ContactHeader /> : null}
 * >
 *   <SheetPage name="main" title="Contact">
 *     <ContactDetails />
 *   </SheetPage>
 *   <SheetPage name="email" title="Email">
 *     <EmailOptions />
 *   </SheetPage>
 * </ResponsiveSheet>
 * ```
 */
export function ResponsiveSheet({
  open,
  onClose,
  children,
  header,
  footer,
  defaultPage = 'main',
  maxWidth,
  placement = 'center',
  modalBreakpoint = 768,
  forceMode,
  className = '',
  panelClassName: panelClassNameProp = '',
  noPanelPadding = false,
  sheetMaxHeight,
  panelStyle,
  hideCloseButton = false,
  glass = false,
  hideScrollIndicator = false,
}: ResponsiveSheetProps) {
  // Fold `glass` into panelClassName up front so every downstream check that
  // sniffs the string (dark chrome, drag handle, close button) keeps working
  // unchanged — the glass string contains `text-white`, which is what they read.
  const panelClassName = glass
    ? `${SHEET_GLASS_PANEL} ${panelClassNameProp}`.trim()
    : panelClassNameProp;
  // Glass sheets get more room: they carry rich content (rosters, maps, forms)
  // and max-w-2xl left them narrow on a desktop.
  const resolvedMaxWidth = maxWidth ?? (glass ? 'max-w-4xl' : 'max-w-3xl');
  const { resolvedTheme } = useTheme();
  const portalContainer = usePortalContainer();
  const [mode, setMode] = useState<SheetMode>('sheet');
  const [currentPage, setCurrentPage] = useState(defaultPage);
  const [history, setHistory] = useState<string[]>([defaultPage]);
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Synthetic actions page state. Set when SheetPageKebab pushes a menu
  // in sheet mode; cleared when the sheet closes. The items live on the
  // ResponsiveSheet (not the kebab) because the kebab itself unmounts
  // when the user navigates away from the parent page.
  const [kebabState, setKebabState] = useState<{
    items: SheetPageKebabItem[];
    title: string;
  } | null>(null);

  // Detect viewport mode (unless the host pinned one)
  useEffect(() => {
    if (forceMode) {
      setMode(forceMode);
      return;
    }
    const checkMode = () => {
      setMode(window.innerWidth >= modalBreakpoint ? 'modal' : 'sheet');
    };

    checkMode();
    window.addEventListener('resize', checkMode);
    return () => window.removeEventListener('resize', checkMode);
  }, [modalBreakpoint, forceMode]);

  // The modal holds the page still behind it (BottomSheet does its own).
  useScrollLock(open && mode === 'modal');

  // Reset navigation when sheet opens or closes
  useEffect(() => {
    if (open) {
      // When opening to a non-main page, seed history with 'main' so back works
      setCurrentPage(defaultPage);
      setHistory(defaultPage === 'main' ? ['main'] : ['main', defaultPage]);
    } else {
      // When closing, reset for cleanup
      setCurrentPage(defaultPage);
      setHistory([defaultPage]);
      // Drop any registered kebab so the next open doesn't carry over
      // stale items from a prior session.
      setKebabState(null);
    }
  }, [open, defaultPage]);

  // Navigation functions
  // A page change starts at the top of the scroll area. Without this, a page
  // opened from deep in a long one (Adjust from the bottom of Giving) inherits
  // the offset and shows blank space where its short content has scrolled past.
  const scrollToTop = useCallback(() => {
    const el = scrollContainerRef.current;
    if (el) el.scrollTop = 0;
  }, []);

  const navigate = useCallback(
    (page: string) => {
      scrollToTop();
      setHistory((prev) => [...prev, page]);
      setCurrentPage(page);
    },
    [scrollToTop]
  );

  const goBack = useCallback(() => {
    // Slicing a history of 2+ always leaves a page to go back to.
    const back = history.length > 1 ? history[history.length - 2] : undefined;
    if (back !== undefined) {
      scrollToTop();
      setHistory(history.slice(0, -1));
      setCurrentPage(back);
    }
  }, [history, scrollToTop]);

  const canGoBack = history.length > 1;

  // The navigator swaps pages with AnimatePresence mode="wait": the new page
  // mounts only after the old one has left, so reset again once it is in.
  useEffect(() => {
    scrollToTop();
    const id = window.setTimeout(scrollToTop, 320);
    return () => window.clearTimeout(id);
  }, [currentPage, scrollToTop]);

  const pushKebabPage = useCallback((items: SheetPageKebabItem[], title = 'Actions') => {
    setKebabState({ items, title });
    // Defer navigation by one frame so the synthetic page is in the
    // pages array before AnimatePresence tries to render it.
    requestAnimationFrame(() => {
      setHistory((prev) => [...prev, KEBAB_PAGE_NAME]);
      setCurrentPage(KEBAB_PAGE_NAME);
    });
  }, []);

  // Check if content is scrollable and update indicator
  const checkScrollIndicator = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const hasMoreContent = container.scrollHeight > container.clientHeight + 5;
    const isAtBottom = container.scrollTop + container.clientHeight >= container.scrollHeight - 20;

    setShowScrollIndicator(hasMoreContent && !isAtBottom);
  }, []);

  // Set up scroll indicator tracking for modal mode
  useEffect(() => {
    if (mode !== 'modal' || !open) return;

    const container = scrollContainerRef.current;
    if (!container) return;

    // Check initially
    checkScrollIndicator();

    // Check after delays to account for content rendering and animations
    const timeouts = [
      setTimeout(checkScrollIndicator, 50),
      setTimeout(checkScrollIndicator, 150),
      setTimeout(checkScrollIndicator, 300),
    ];

    // Listen for scroll events
    container.addEventListener('scroll', checkScrollIndicator);
    window.addEventListener('resize', checkScrollIndicator);

    // Use ResizeObserver to detect content size changes
    const resizeObserver = new ResizeObserver(checkScrollIndicator);
    resizeObserver.observe(container);
    // Also observe the first child if it exists
    if (container.firstElementChild) {
      resizeObserver.observe(container.firstElementChild);
    }

    return () => {
      timeouts.forEach(clearTimeout);
      container.removeEventListener('scroll', checkScrollIndicator);
      window.removeEventListener('resize', checkScrollIndicator);
      resizeObserver.disconnect();
    };
  }, [mode, open, currentPage, checkScrollIndicator]);

  // Extract SheetPage children
  const pages: {
    name: string;
    title?: ReactNode;
    header?: ReactNode;
    headerActions?: ReactNode;
    backBarTrailing?: ReactNode;
    content: ReactNode;
  }[] = [];
  Children.forEach(children, (child) => {
    if (isValidElement(child) && child.type === SheetPage) {
      const {
        name,
        title,
        header: pageHeader,
        headerActions,
        backBarTrailing,
        children: pageChildren,
      } = child.props as SheetPageProps;
      pages.push({
        name,
        title,
        header: pageHeader,
        headerActions,
        backBarTrailing,
        content: pageChildren,
      });
    }
  });

  // Synthetic actions page — appended only while a kebab has registered
  // items. Header is intentionally null so the parent page's gradient
  // header (or sheet-level header) keeps showing — the user stays
  // anchored in the same context while picking an action. Title shows
  // in the back-strip via `canGoBack`.
  if (kebabState) {
    pages.push({
      name: KEBAB_PAGE_NAME,
      title: kebabState.title,
      content: (
        <KebabActionsPageContent
          items={kebabState.items}
          onSelected={() => {
            // Pop back to the parent page after an action runs. Consumers
            // can override by closing the whole sheet from inside their
            // onSelect (which resets history anyway).
            const back = history.length > 1 ? history[history.length - 2] : undefined;
            if (back !== undefined) {
              setHistory(history.slice(0, -1));
              setCurrentPage(back);
            }
          }}
        />
      ),
    });
  }

  const activePage = pages.find((p) => p.name === currentPage);
  // Active page's header actions (if any) — rendered in the top-right
  // liquid-glass slot, replacing the default close button when present.
  const activeHeaderActions = activePage?.headerActions;
  // Active page's own header overrides the sheet-level `header` prop so
  // drilled-in pages can carry their own hierarchy (kicker + title).
  // Special case: the synthetic kebab page inherits the previous page's
  // header so the user keeps the brand/gradient context they tapped from
  // — the actions list slides in below the same chrome rather than
  // ripping it away.
  const onKebabPage = currentPage === KEBAB_PAGE_NAME;
  const inheritedKebabHeader =
    onKebabPage && history.length >= 2
      ? pages.find((p) => p.name === history[history.length - 2])?.header
      : undefined;
  const activePageHeader = inheritedKebabHeader ?? activePage?.header;

  // Context value
  const contextValue: ResponsiveSheetContextValue = {
    mode,
    resolvedTheme,
    navigate,
    goBack,
    currentPage,
    canGoBack,
    pushKebabPage,
  };

  // Resolve header content. Priority: the active page's `header` prop
  // (for per-page hierarchy) falls back to the sheet-level `header` prop.
  const sheetLevelHeader = typeof header === 'function' ? header(contextValue) : header;
  const resolvedHeader = activePageHeader ?? sheetLevelHeader;

  // Detect CollapsibleHeader marker
  const isCollapsible =
    isValidElement(resolvedHeader) &&
    (resolvedHeader.type as { __isCollapsibleHeader?: boolean }).__isCollapsibleHeader === true;

  const collapsibleProps = isCollapsible ? (resolvedHeader.props as CollapsibleHeaderProps) : null;

  const collapsibleRatio = collapsibleProps?.ratio ?? 0.5;

  // Collapsible header hook — only active in sheet mode with a CollapsibleHeader
  const {
    wrapperRef,
    contentCallbackRef,
    handleScroll: handleCollapsibleScroll,
    reset: resetCollapsible,
  } = useCollapsibleHeader({
    enabled: isCollapsible && mode === 'sheet',
    ratio: collapsibleRatio,
  });

  // Reset collapsible header on page navigation
  useEffect(() => {
    if (isCollapsible && mode === 'sheet') {
      resetCollapsible();
    }
  }, [currentPage, isCollapsible, mode, resetCollapsible]);

  // Liquid-glass overlay pill with the active page's trailing actions.
  // Floats over the top-right of the header in both sheet & modal modes.
  const actionsOverlay = activeHeaderActions ? (
    <div className="pointer-events-none absolute top-3 right-3 z-30 md:top-4 md:right-4">
      <div className="pointer-events-auto flex items-center gap-1 rounded-full border border-white/30 bg-white/20 p-0.5 text-white shadow-sm backdrop-blur-md">
        {activeHeaderActions}
      </div>
    </div>
  ) : null;

  // Wraps a header with the actions overlay when applicable. Using a
  // relative wrapper so absolute children position against the header box.
  const wrapHeaderWithActions = (node: ReactNode): ReactNode =>
    activeHeaderActions ? (
      <div className="relative">
        {node}
        {actionsOverlay}
      </div>
    ) : (
      node
    );

  // Build the actual header DOM for sheet mode when collapsible
  const sheetHeader = wrapHeaderWithActions(
    isCollapsible && collapsibleProps ? (
      <div ref={wrapperRef} className="relative overflow-hidden">
        {/* Background layer (e.g. blurred image) — fills entire container */}
        {collapsibleProps.background && (
          <div className="absolute inset-0">{collapsibleProps.background}</div>
        )}
        {/* Sharp content — scales down on scroll */}
        <div ref={contentCallbackRef} className="relative origin-top">
          {collapsibleProps.children}
        </div>
      </div>
    ) : (
      resolvedHeader
    )
  );

  // For modal mode, CollapsibleHeader just renders its children directly
  const modalHeader = wrapHeaderWithActions(
    isCollapsible && collapsibleProps ? collapsibleProps.children : resolvedHeader
  );

  // Sheet mode (mobile)
  if (mode === 'sheet') {
    return (
      <ResponsiveSheetContext.Provider value={contextValue}>
        <BottomSheet
          open={open}
          onClose={onClose}
          className={withPanelBg(panelClassName)}
          header={sheetHeader}
          hideHandle={false}
          showScrollIndicator={!hideScrollIndicator}
          maxHeight={sheetMaxHeight}
          panelStyle={panelStyle}
          lightScrollIndicator={
            panelClassName?.includes('bg-card') || panelClassName?.includes('bg-white')
          }
          onContentScroll={isCollapsible ? handleCollapsibleScroll : undefined}
          resetScrollKey={currentPage}
          footer={footer}
        >
          <SheetContent
            pages={pages}
            currentPage={currentPage}
            canGoBack={canGoBack}
            onBack={goBack}
            mode={mode}
            hasHeader={!!sheetHeader}
            darkPanel={panelClassName?.includes('text-white')}
          />
        </BottomSheet>
      </ResponsiveSheetContext.Provider>
    );
  }

  // Panel always carries a background (see withPanelBg).
  const panelClass = withPanelBg(panelClassName);

  // Modal mode (desktop)
  const modal = (
    <ResponsiveSheetContext.Provider value={contextValue}>
      <AnimatePresence>
        {open && (
          <div
            className={`fixed inset-0 z-50 flex ${placement === 'right' ? 'items-stretch justify-end' : 'items-center justify-center'}`}
          >
            {/* Backdrop — dim + blur the page behind (base behavior for all
                sheets). Animate the blur radius + dim color rather than
                opacity: fading opacity on a backdrop-filter element isolates
                it into an offscreen group, so the page-blur wouldn't appear
                until the fade completed. Keeping opacity at 1 lets the blur
                ramp in from the first frame. */}
            <motion.div
              initial={{ backgroundColor: 'rgba(0,0,0,0)', backdropFilter: 'blur(0px)' }}
              animate={{ backgroundColor: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(4px)' }}
              exit={{ backgroundColor: 'rgba(0,0,0,0)', backdropFilter: 'blur(0px)' }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0"
              onClick={onClose}
            />

            {/* Modal panel */}
            <motion.div
              initial={placement === 'right' ? { x: '100%' } : { opacity: 0, scale: 0.95 }}
              animate={placement === 'right' ? { x: 0 } : { opacity: 1, scale: 1 }}
              exit={placement === 'right' ? { x: '100%' } : { opacity: 0, scale: 0.95 }}
              transition={
                placement === 'right'
                  ? { type: 'spring', stiffness: 380, damping: 38 }
                  : { duration: 0.2 }
              }
              className={`relative z-10 flex w-full flex-col overflow-hidden shadow-2xl ${
                placement === 'right' ? 'h-full max-h-none rounded-r-none!' : 'mx-4 max-h-[90vh]'
              } ${noPanelPadding ? '' : 'p-4'} ${resolvedMaxWidth} ${panelClass} ${className}`}
              style={panelStyle}
            >
              {/* Close button — hidden when the active SheetPage supplies
                  `headerActions` (the actions live in the header's top-right
                  liquid-glass slot, and modal backdrop click still closes). */}
              {!hideCloseButton && !activeHeaderActions && (
                <button
                  onClick={onClose}
                  className={`absolute z-20 p-1 text-white/50 transition-colors hover:text-white ${noPanelPadding ? 'top-4 right-4' : 'top-2 right-2'}`}
                >
                  <X className="h-5 w-5" />
                </button>
              )}

              {/* Header (rendered in modal — CollapsibleHeader renders children directly) */}
              {modalHeader && (
                <div className={`shrink-0 ${noPanelPadding ? '' : 'pt-2'}`}>{modalHeader}</div>
              )}

              {/* Content - scrollable area */}
              <div
                ref={scrollContainerRef}
                className="scrollbar-hide min-h-0 flex-1 overflow-y-auto"
              >
                <SheetContent
                  hasHeader={!!modalHeader}
                  pages={pages}
                  currentPage={currentPage}
                  canGoBack={canGoBack}
                  onBack={goBack}
                  mode={mode}
                  darkPanel={panelClassName?.includes('text-white')}
                />
              </div>

              {/* Pinned footer — outside the scroll area so it stays put, and
                  above the scroll indicator, which anchors to the panel's
                  bottom edge (now the footer's own edge). */}
              {footer && <div className="relative z-20 shrink-0">{footer}</div>}

              {/* Scroll indicator - shows when more content below. Suppressed
                  behind a footer, which covers where this gradient anchors and
                  is its own bottom boundary. */}
              <AnimatePresence>
                {showScrollIndicator && !footer && !hideScrollIndicator && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-20"
                    style={{
                      background: panelClassName?.includes('bg-card')
                        ? // Token-aware: bg-card panels fade into the card color,
                          // correct in both light and dark themes.
                          'linear-gradient(to top, var(--card) 0%, var(--card) 30%, color-mix(in oklab, var(--card) 80%, transparent) 50%, transparent 100%)'
                        : panelClassName?.includes('bg-white')
                          ? 'linear-gradient(to top, white 0%, white 30%, rgba(255,255,255,0.8) 50%, transparent 100%)'
                          : 'linear-gradient(to top, #1f2220 0%, #1f2220 30%, rgba(31,34,32,0.8) 50%, transparent 100%)',
                    }}
                  >
                    <div
                      className={`absolute inset-x-0 bottom-2 flex flex-col items-center gap-0.5 ${
                        panelClassName?.includes('bg-card') || panelClassName?.includes('bg-white')
                          ? 'text-muted-foreground'
                          : 'text-white/50'
                      }`}
                    >
                      <span className="text-[10px] font-medium tracking-wider uppercase">
                        Scroll for more
                      </span>
                      <motion.div
                        animate={{ y: [0, 3, 0] }}
                        transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <ChevronDown className="h-3 w-3" />
                      </motion.div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </ResponsiveSheetContext.Provider>
  );

  // Portal the modal to the portal container when provided (e.g. shadow DOM
  // widgets). Otherwise portal to document.body so the backdrop sits above ALL
  // page chrome — sticky PageBars, the nav rail, etc. — and blurs everything
  // behind it. Rendering inline would trap it inside the page's stacking
  // context (e.g. a transformed SlideView), letting a z-30 PageBar paint over
  // the backdrop and escape the blur.
  if (portalContainer) {
    return createPortal(modal, portalContainer);
  }
  if (typeof document !== 'undefined') {
    return createPortal(modal, document.body);
  }

  return modal;
}

// ============================================================================
// KebabActionsPageContent — full-width row picker for the synthetic
// __sheetKebabActions page. Mirrors SheetPageKebab's dropdown styling
// (sections, tone, selected, destructive) but as tap-friendly rows
// suited to a mobile sheet. NN/g + M3 endorse this drill-down pattern
// over a popover-over-sheet for "more actions" on mobile.
// ============================================================================

const KEBAB_TONE_CLASSES: Record<
  NonNullable<SheetPageKebabItem['tone']>,
  { text: string; selectedBg: string; iconText: string }
> = {
  primary: {
    text: 'text-primary',
    selectedBg: 'bg-primary/10',
    iconText: 'text-primary',
  },
  amber: {
    text: 'text-amber-700 dark:text-amber-300',
    selectedBg: 'bg-amber-100 dark:bg-amber-950/40',
    iconText: 'text-amber-700 dark:text-amber-300',
  },
  red: {
    text: 'text-red-700 dark:text-red-300',
    selectedBg: 'bg-red-100 dark:bg-red-950/40',
    iconText: 'text-red-700 dark:text-red-300',
  },
};

export function KebabActionsPageContent({
  items,
  onSelected,
}: {
  items: SheetPageKebabItem[];
  onSelected: () => void;
}) {
  // Group consecutive items by section so each group renders as its own
  // bordered card — iOS settings / Apple Files convention. Items without
  // a `section` collapse into a single trailing card. Cards float on the
  // sheet's bg with gap-3 between, which gives the visual containment
  // the bare-rows approach was missing.
  type Group = { section?: string; items: SheetPageKebabItem[] };
  const groups: Group[] = [];
  for (const item of items) {
    const last = groups[groups.length - 1];
    if (last && last.section === item.section) {
      last.items.push(item);
    } else {
      groups.push({ section: item.section, items: [item] });
    }
  }

  return (
    <div className="flex flex-col gap-3 p-4">
      {groups.map((group, gi) => (
        <div key={gi} className="bg-card overflow-hidden border">
          {group.section && (
            <div className="text-muted-foreground/70 bg-muted/30 border-b px-4 py-2 text-[10px] font-semibold tracking-widest uppercase">
              {group.section}
            </div>
          )}
          {group.items.map((item) => {
            const Icon = item.icon;
            const tone = item.tone ? KEBAB_TONE_CLASSES[item.tone] : null;
            const isDestructive = item.variant === 'destructive';
            return (
              <button
                key={item.key}
                type="button"
                onClick={() => {
                  item.onSelect();
                  onSelected();
                }}
                className={cn(
                  'flex w-full items-center gap-3 border-b px-4 py-4 text-left transition-colors last:border-b-0',
                  'active:scale-[0.995]',
                  item.selected ? 'font-semibold' : 'font-medium',
                  tone
                    ? cn(tone.text, item.selected && tone.selectedBg, 'hover:bg-muted/40')
                    : isDestructive
                      ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/20'
                      : 'text-foreground hover:bg-muted/40',
                  item.selected && !tone && !isDestructive && 'bg-muted/40'
                )}
              >
                <Icon
                  className={cn(
                    'h-5 w-5 shrink-0',
                    tone
                      ? tone.iconText
                      : isDestructive
                        ? 'text-red-600 dark:text-red-400'
                        : 'text-muted-foreground'
                  )}
                />
                <span className="flex-1 text-sm">{item.label}</span>
                {item.selected && (
                  <Check
                    className={cn(
                      'h-4 w-4 shrink-0 opacity-70',
                      tone ? tone.iconText : 'text-muted-foreground'
                    )}
                  />
                )}
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

// Re-export types for consumers (SheetPageProps is re-exported at top from SheetNavigator)
export type { ResponsiveSheetProps, ResponsiveSheetContextValue, SheetMode };
